// TANDA 28b (firma #230, diseño de referidos v1 §5) — LA PLOMERÍA DE REFERIDOS.
//
// Vive aquí y no dentro de una pantalla por la misma razón que `lib/groups.js`,
// que es su molde declarado: seis superficies distintas la necesitan —la página
// `/r/CODIGO`, el reclamo silencioso del chasis, `InvitaAmigo` (en línea y en
// modal), la franja de `/upgrade`, la píldora del menú y el aviso del Panel— y
// así se puede probar sin montar React.
//
// ── LAS TRES COMPUERTAS, EN ESTE ORDEN ──────────────────────────────────────
//
//  1. `IS_APP_BUILD` (#219): en la app nativa NADA de referidos ocurre. No es
//     eliminación de código —el módulo viaja entero en el `.ipa`— sino una
//     compuerta de EJECUCIÓN: cada función que toca la red sale por lo bajo
//     antes de pedir nada. Un descuento que la web aplica y la App Store no ve
//     es justo lo que la regla 3.1.1 no perdona.
//  2. La bandera `REFERRALS_ENABLED`, que nace apagada (#256). El backend ya la
//     obedece; aquí lo que importa es que su respuesta apagada y un fallo de red
//     se vean IGUAL desde arriba.
//  3. El fallo. Cualquier no-200, cualquier timeout, cualquier 5xx devuelve el
//     mismo objeto neutro que la bandera apagada.
//
// De ahí que ninguna de estas funciones rechace jamás: quien las llama pinta
// superficies opcionales, y una superficie opcional que tira la pantalla por un
// 500 es peor que no existir. El fallo va SIEMPRE hacia «referidos no existe».

import { useReducer, useEffect } from 'react';
import { IS_APP_BUILD } from './buildTarget';
import { toast } from 'sonner';

/* ── EL CLIENTE HTTP SE PIDE AL LLAMAR, NO AL IMPORTAR ──────────────────────
 *
 * Es el MISMO `lib/api` que usa todo el repo —con su sesión, sus cookies y su
 * cadena de reintento—; lo único distinto es CUÁNDO se resuelve el módulo.
 *
 * POR QUÉ, y es un hecho medido, no una precaución: `lib/api.js:2` importa
 * `../i18n`, y `src/i18n.js:9` llama a `.use(initReactI18next)` en el cuerpo del
 * módulo. Las suites que doblan `react-i18next` con una factory que no exporta
 * `initReactI18next` —que son casi todas— revientan al ARRANCAR en cuanto algo
 * de su árbol alcanza `lib/api` sin doblarlo. Este módulo lo consumen ahora el
 * menú lateral y el nav de abajo, que 28 suites montan sin doblar `lib/api`: con
 * el import arriba, esas 28 se caían antes de correr un solo test.
 *
 * La llamada perezosa deja el grafo de módulos igual que antes de esta tanda
 * para quien sólo IMPORTA referidos, y entrega exactamente el mismo cliente a
 * quien de verdad va a la red. `jest.mock('./api')` lo sigue interceptando. */
function cliente() {
  return require('./api').default;
}

function baseDelSitio() {
  return require('./api').API_BASE;
}

// LA MISMA VARIABLE DEL BUILD que ya leen `components/Sidebar.js:14` y
// `pages/DiagReadyChainPage.js:66`, y se lee AQUÍ y no desde `routeGuards.js`
// por una razón de dependencias: `routeGuards` importa `react-router-dom` y el
// contexto de sesión, así que traérselo metería el router entero dentro de un
// módulo de plomería que hoy se puede probar sin montar React.
//
// No rompe el candado de la TANDA 12 (`lib/correoAdminUnico.tanda12.test.js`):
// lo que ése prohíbe es una DIRECCIÓN escrita en duro, no una segunda lectura de
// la variable — el propio letrero de `routeGuards.js` deja dicho que las otras
// dos lecturas directas son a propósito y que el valor es el mismo.
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || '';

// LA LLAVE LOCAL DEL DEEP LINK. Cuando alguien abre /r/CODIGO SIN sesión, el
// código se guarda aquí y se reclama en cuanto entre por cualquier puerta. Es
// hermana de `PENDING_JOIN_KEY` (groups.js:17) y deliberadamente distinta: los
// dos guardados conviven sin orden entre ellos, y cada flujo corre lo suyo.
export const REF_STASH_KEY = 'sb_pending_ref_code';

// El código son 8 caracteres (diseño §5). El tope de 32 es holgura para lo que
// venga pegado de un WhatsApp, no una promesa sobre el largo real.
export const REF_CODE_MAX = 32;

// El objeto que significa «referidos no existe para ti»: bandera apagada, app
// nativa, red caída o 5xx. Una sola forma para los cuatro casos, para que
// ninguna superficie pueda distinguirlos y ponerse creativa.
const ESTADO_APAGADO = Object.freeze({
  enabled: false, discount_pending: 0, referred: false, prizes_earned: 0,
});

// Los motivos que `POST /claim` firma (services/referrals.py: MOTIVOS). Lo que
// no esté aquí cae a `other` y comparte el toast genérico: un motivo nuevo del
// backend no puede hacer que el frontend pinte `undefined`.
const MOTIVOS = ['own_code', 'already_paid', 'already_referred', 'unknown_code', 'disabled'];

/** Mayúsculas, sin UN solo espacio en blanco, recortado a 32.
 *
 *  Se quita TODO espacio interno y no sólo los extremos: el código llega pegado
 *  de un chat y «ABCD 2345» es el mismo código que «ABCD2345» para cualquiera
 *  que no sea una comparación de cadenas. El backend normaliza igual
 *  (`_normalizar`), así que lo que se ve y lo que viaja son lo mismo. */
export function normalizarCodigo(s) {
  return String(s ?? '').replace(/\s+/g, '').toUpperCase().slice(0, REF_CODE_MAX);
}

export function stashPendingReferralCode(code) {
  const codigo = normalizarCodigo(code);
  if (!codigo) return;
  try {
    localStorage.setItem(REF_STASH_KEY, codigo);
  } catch (e) { /* Safari privado: la comodidad se pierde, nada se rompe */ }
}

export function readPendingReferralCode() {
  try {
    return localStorage.getItem(REF_STASH_KEY) || null;
  } catch (e) { return null; }
}

export function clearPendingReferralCode() {
  try { localStorage.removeItem(REF_STASH_KEY); } catch (e) { /* idem */ }
}

/** El enlace que se reparte. Sale de `API_BASE` —la constante quemada
 *  `https://quizessa.com` (lib/api.js:18): y NUNCA de `window.location`: si
 *  saliera de la ventana, el enlace copiado desde un preview de Cloudflare
 *  apuntaría al preview, y el amigo aterrizaría en un sitio que mañana no
 *  existe. Misma regla y mismo porqué que `joinLinkFor` (groups.js). */
export function referralLinkFor(code) {
  return `${baseDelSitio()}/r/${code}`;
}

// ── EL MEMO DE `/state` ──────────────────────────────────────────────────────
//
// UNA petición por CARGA DE PÁGINA, compartida. Desde que la píldora `-20%` vive
// en el menú lateral y en el nav de abajo, hay cuatro consumidores del mismo
// dato en una sola pantalla: `Sidebar`, `MobileBottomNav`, `Dashboard` y
// `PricingPage`. Sin memo eso son cuatro GET idénticos por aterrizaje, y los dos
// primeros son del CHASIS, que se monta una vez por carga y no se vuelve a
// montar al navegar: pedirían de nuevo en cada pantalla que sí re-monta.
//
// Se guarda LA PROMESA, no el valor: dos montajes simultáneos —que es lo que
// pasa siempre, porque el chasis y la página se montan en el mismo tick—
// comparten el vuelo en lugar de abrir dos.
//
// NO ES UN CACHE DE SESIÓN: muere con la carga de página, y `claimReferralCode`
// lo tira al aplicar un código, porque justo ahí el estado dejó de ser cierto.
//
// ── LA CUENTA VA DENTRO DE LA LLAVE (28b-fix, hallazgo H-1) ─────────────────
//
// «Una carga de página» y «una sesión» NO son lo mismo en una SPA: cerrar
// sesión y entrar con otra cuenta no recarga nada. Con una sola promesa suelta,
// el usuario B recibía el objeto de A —con su `discount_pending`— y no salía ni
// una petición: etiqueta «-20%», franja y tachado prometidos a quien no tiene
// descuento. El cupón lo pone el backend en el checkout, así que el cobro nunca
// estuvo en juego; la mentira en pantalla sí, y es justo la que #257 evitó.
//
// Por eso el memo es `{ userId, promise }` y no una promesa a secas: la
// identidad forma parte de la llave. Se guarda LA ÚLTIMA cuenta, no un
// historial — volver a A después de pasar por B vuelve a pedir, porque un dato
// pedido antes del cambio ya no se puede afirmar.
let memoState = null;

/** Tira el memo. La llama el reclamo (el pendiente acaba de cambiar) y las
 *  pruebas, que necesitan cada caso limpio. */
export function resetReferralState() {
  memoState = null;
}

async function pedirState() {
  try {
    const res = await cliente().get('/referrals/state');
    const d = res?.data || {};
    return {
      enabled: Boolean(d.enabled),
      discount_pending: Number(d.discount_pending) || 0,
      referred: Boolean(d.referred),
      prizes_earned: Number(d.prizes_earned) || 0,
    };
  } catch (e) {
    // 401 (sin sesión), 404, 5xx, red caída: todos son «no existe». Sin
    // `console.error`: esta llamada es opcional y no debe ensuciar la consola
    // de nadie — el candado de bandera apagada lo fija.
    return ESTADO_APAGADO;
  }
}

/** `{enabled, discount_pending, referred, prizes_earned}`, nunca rechaza.
 *
 *  `userId` ES OBLIGATORIO y es el id de la CUENTA (`user.id` del contexto de
 *  sesión, el mismo objeto que ya leen los seis consumidores). Sin él —sesión
 *  todavía sin resolver, o un invitado sin id— se devuelve el apagado SIN tocar
 *  la red y SIN escribir el memo: una respuesta que no se sabe de quién es no
 *  puede quedarse guardada para el siguiente que pregunte. */
export function fetchReferralState(userId) {
  if (IS_APP_BUILD) return Promise.resolve(ESTADO_APAGADO);
  if (!userId) return Promise.resolve(ESTADO_APAGADO);
  if (!memoState || memoState.userId !== userId) {
    memoState = { userId, promise: pedirState() };
  }
  return memoState.promise;
}

/** El código propio. Tres formas de terminar, y las tres son normales:
 *
 *   · `ok`       — hay código (lo crea el backend la primera vez que se pide).
 *   · `guest`    — 403: un invitado no tiene enlace propio (#230). SÍ puede
 *                  reclamar, así que la superficie le pinta el campo del código.
 *   · `disabled` — 404 de bandera apagada, y también cualquier otro fallo: para
 *                  el componente son lo mismo (devolver `null`).
 *
 * NO lleva memo, y es a propósito: `/me` CREA el código, y F.2 lo vuelve a
 * pedir justo después de aplicar uno para que el campo desaparezca. */
export async function fetchReferralMe() {
  if (IS_APP_BUILD) return { kind: 'disabled' };
  try {
    const res = await cliente().get('/referrals/me');
    const d = res?.data || {};
    return {
      kind: 'ok',
      data: {
        code: d.code || '',
        prizes_earned: Number(d.prizes_earned) || 0,
        prizes_cap: Number(d.prizes_cap) || 0,
        discount_pending: Number(d.discount_pending) || 0,
      },
    };
  } catch (e) {
    if (e?.response?.status === 403) return { kind: 'guest' };
    return { kind: 'disabled' };
  }
}

/** PÚBLICO: `/r/CODIGO` tiene que pintar «Fulano te invitó» antes de que exista
 *  una sesión. Con la bandera apagada el backend contesta `valid:false`, que es
 *  lo mismo que un código que no existe — a propósito: desde fuera no se puede
 *  saber si el programa existe. */
export async function lookupReferralCode(code) {
  if (IS_APP_BUILD) return { valid: false };
  try {
    const res = await cliente().get(`/referrals/lookup/${encodeURIComponent(normalizarCodigo(code))}`);
    const d = res?.data || {};
    return { valid: Boolean(d.valid), inviter_first_name: d.inviter_first_name || null };
  } catch (e) {
    return { valid: false };
  }
}

/** El ÚNICO sitio donde se reclama, venga del enlace o tecleado a mano.
 *
 *  Nunca lanza: devuelve `{ok:true}` o `{ok:false, reason}`. El motivo sale del
 *  `detail` de FastAPI (409 con el motivo, 404 `disabled`); cualquier otra cosa
 *  —incluida la red caída— es `other`, que comparte el toast genérico. */
export async function claimReferralCode(code) {
  if (IS_APP_BUILD) return { ok: false, reason: 'disabled' };
  try {
    await cliente().post('/referrals/claim', { code: normalizarCodigo(code) });
    // El pendiente y la marca `referred` acaban de cambiar: lo que el memo
    // guarda ya no es cierto para la siguiente superficie que pregunte.
    resetReferralState();
    return { ok: true };
  } catch (e) {
    const detail = e?.response?.data?.detail;
    return { ok: false, reason: MOTIVOS.includes(detail) ? detail : 'other' };
  }
}

/** El toast que le toca al resultado, con la clave de dedup (#239).
 *
 *  El `id` de Sonner es lo que impide que dos superficies —o dos corridas del
 *  mismo efecto— apilen el mismo aviso: con el mismo `id`, el segundo REEMPLAZA
 *  al primero en vez de sumarse. Sólo `own_code` tiene copy propio (Anexo A.6);
 *  los otros cuatro motivos comparten el genérico, porque decirle al usuario
 *  «ya pagaste» o «ya estás referido» no le da ninguna acción que tomar. */
export function toastPorClaim(result, t) {
  if (result?.ok) {
    toast.success(t('common:referral.toast_ok'), { id: 'referral_claim_ok' });
    return;
  }
  if (result?.reason === 'own_code') {
    toast.error(t('common:referral.toast_own_code'), { id: 'referral_own_code' });
    return;
  }
  toast.error(t('common:referral.toast_fail'), { id: 'referral_claim_fail' });
}

/** El reclamo silencioso, el que corre al entrar por cualquier puerta.
 *
 *  EL ORDEN ES EL MECANISMO Y NO SE PUEDE REORDENAR: se borra el guardado ANTES
 *  de llamar a la red. `index.js` monta la app dentro de `<React.StrictMode>`,
 *  que hace montar → limpiar → montar cada efecto; con el borrado detrás de la
 *  red, la corrida #2 leería el código todavía puesto y saldrían dos peticiones
 *  y dos toasts por un solo enlace. Con el borrado delante, la #2 lee `null` y
 *  no hace nada — sin banderas de módulo y sin depender de quién resuelve
 *  primero. Es la misma receta que `markPendingJoinPrompted` usa en el empujón
 *  de grupos, escrita con el guardado en vez de con una marca aparte.
 *
 *  EL PRECIO ESTÁ ACEPTADO EN EL DISEÑO (§3): «el frontend llama una vez y borra
 *  el guardado, con éxito o sin él». Si la red se cae justo ahí, el código se
 *  pierde y hay que volver a abrir el enlace — o teclearlo en Ajustes, que es
 *  el respaldo que existe para esto.
 *
 *  NUNCA LANZA. Corre dentro del chasis autenticado, al lado del empujón de
 *  grupos: una excepción aquí se llevaría por delante la pantalla entera. */
export async function reclamarPendienteSiHay(t) {
  if (IS_APP_BUILD) return;
  const code = readPendingReferralCode();
  if (!code) return;
  clearPendingReferralCode();
  try {
    const res = await claimReferralCode(code);
    toastPorClaim(res, t);
  } catch (e) {
    // `claimReferralCode` ya no rechaza; esto es el cinturón por si mañana
    // alguien le mete un `throw`. Una vez, y a `warn`: no es un error del
    // usuario ni algo que él pueda arreglar.
    console.warn('[referidos] el reclamo pendiente no se pudo completar', e);
  }
}

// ── LOS ESTADOS DE VISTA (bloque I) ─────────────────────────────────────────
//
// `?vista=` deja a Carlos ver las cuatro caras del programa sin sembrar datos en
// Mongo ni pagar un cobro real. NO es una superficie de producto: es un molde
// para el smoke, y por eso la puerta es la MISMA con la que el frontend decide
// mostrar la entrada a `/sb-ctrl` (Sidebar.js:105 y routeGuards.js:93), más la
// compuerta de web. Para cualquier otro usuario `?vista=` no existe.
//
// En modo vista NO se llama a ningún `/api/referrals/*` —ni siquiera al memo—
// porque el punto es ver la cara sin tener el estado: pedirla devolvería la
// verdad de la cuenta de Carlos y taparía justo lo que se quiere mirar.

/** ¿Esta sesión puede usar `?vista=`? Toma el usuario porque es una función de
 *  módulo y no puede leer el contexto de React; quien la llama ya tiene el
 *  `user` de `useAuth()`, que es de donde salen las otras dos comprobaciones de
 *  administrador del repo. */
export function esVistaAdmin(user) {
  if (IS_APP_BUILD) return false;
  return !!ADMIN_EMAIL && user?.email?.toLowerCase() === ADMIN_EMAIL;
}

const VISTAS = {
  amigo: {
    state: { enabled: true, discount_pending: 1, referred: true, prizes_earned: 0 },
    me: { code: 'PRUEBA23', prizes_earned: 0, prizes_cap: 5, discount_pending: 1 },
  },
  premio: {
    state: { enabled: true, discount_pending: 1, referred: false, prizes_earned: 1 },
    me: { code: 'PRUEBA23', prizes_earned: 1, prizes_cap: 5, discount_pending: 1 },
  },
  tope: {
    state: { enabled: true, discount_pending: 0, referred: false, prizes_earned: 5 },
    me: { code: 'PRUEBA23', prizes_earned: 5, prizes_cap: 5, discount_pending: 0 },
  },
  aviso: {
    state: { enabled: true, discount_pending: 0, referred: false, prizes_earned: 0 },
    me: { code: 'PRUEBA23', prizes_earned: 0, prizes_cap: 5, discount_pending: 0 },
  },
  /* TANDA 28d (§E) — EL QUINTO: el amigo que ACABA de pagar.
     Existe por una razón concreta: la ventana «¡Ya eres Pro!» —y la línea de
     invitar que vive DENTRO de ella, la superficie 6 de las 8 del diseño— sólo
     se puede ver pagando $8.99 de verdad y volviendo de Stripe con un
     `session_id` vivo. Es la cara más cara de mirar de todo el programa.
     `discount_pending: 0` porque su descuento se acaba de consumir en ese cobro,
     y `referred: true` porque vino invitado. Ésa es la ÚNICA diferencia con
     `aviso`, y se nota en un solo sitio (`InvitaAmigo.js:215`, que le esconde el
     campo de «¿tienes un código?»): justo lo que le toca a quien ya usó el suyo.
     El `me` es el de `amigo`: la ventana la abre alguien que sí tiene enlace. */
  exito: {
    state: { enabled: true, discount_pending: 0, referred: true, prizes_earned: 0 },
    me: { code: 'PRUEBA23', prizes_earned: 0, prizes_cap: 5, discount_pending: 1 },
  },
};

/** Los datos de mentira de una vista, o `null` si el valor no es de los cinco
 *  firmados (un `?vista=loquesea` se comporta como si no viniera). */
export function datosDeVista(vista) {
  const v = VISTAS[String(vista || '')];
  if (!v) return null;
  return { state: { ...v.state }, me: { ...v.me } };
}

// ── LA VISTA VIVE MIENTRAS VIVE LA PESTAÑA (28b-fix, hallazgo H-4) ──────────
//
// ANTES: el parámetro se congelaba en una `useRef` al montar. Eso partía el
// mundo en dos de forma asimétrica y silenciosa. El CHASIS (menú y nav de
// abajo) se monta UNA vez por carga de página y se quedaba con el valor para
// siempre; las PÁGINAS se re-montan al navegar y leían la URL nueva, que ya no
// traía el parámetro. De `/dashboard?vista=amigo` a `/upgrade` por un clic:
// píldora sí, franja no. Un smoke hecho navegando —o con un autocompletado de
// Chrome de por medio— veía media cara de cada estado sin forma de notarlo.
//
// AHORA: la URL sigue mandando, pero lo que trae se GUARDA en `sessionStorage`,
// que muere al cerrar la pestaña y no viaja a otra. Así la vista sobrevive a un
// clic, a un «atrás» y a la navegación dentro de la aplicación, y sigue sin
// existir en la siguiente sesión del navegador.
//
// `?vista=off` la borra, y el letrero (`components/LetreroVista.js`) hace lo
// mismo desde su ×.
//
// EL PARÁMETRO SE LEE DE `window.location`, NO DE `useLocation()`. Es una
// desviación consciente del encargo y tiene una razón medida: este módulo es
// plomería y hoy se puede probar sin montar React (ver el letrero de
// `ADMIN_EMAIL` arriba). Meterle `react-router-dom` rompe 52 suites que doblan
// el router sin `useLocation` —50 de ellas fuera de esta tanda—; medido con el
// import puesto: 4 de 4 suites de muestra en rojo, 47 pruebas. El router ya
// actualiza `window.location` con `pushState` ANTES de re-pintar, así que leerlo
// en cada render da el mismo resultado observable sin arrastrar el router aquí.
export const VISTA_STASH_KEY = 'sb_vista_ref';

function leerVistaGuardada() {
  try { return sessionStorage.getItem(VISTA_STASH_KEY) || ''; } catch (e) { return ''; }
}

function guardarVista(vista) {
  try { sessionStorage.setItem(VISTA_STASH_KEY, vista); } catch (e) { /* Safari privado */ }
}

function borrarVistaGuardada() {
  try { sessionStorage.removeItem(VISTA_STASH_KEY); } catch (e) { /* idem */ }
}

/* LOS OYENTES DEL APAGADO. Apagar la vista desde la × del letrero tiene que
 * repintar las CUATRO superficies a la vez, y ninguna comparte árbol con las
 * otras: el menú y el nav viven en el chasis, la franja y el tachado en la
 * página. Un aviso de módulo es lo que las alcanza a todas sin inventar un
 * contexto nuevo ni obligar a cada una a suscribirse al router. */
const oyentesDeVista = new Set();

/** Apaga la vista: lo MISMO que `?vista=off`.
 *
 *  QUITA EL PARÁMETRO DE LA BARRA DE DIRECCIONES, y no es cosmético: la URL es
 *  la primera fuente, así que dejarla con `?vista=amigo` puesto haría que el
 *  siguiente render volviera a encender lo que la × acaba de apagar. Al quitarlo
 *  también sobrevive a un F5, que es lo que cualquiera espera de un botón de
 *  cerrar. Se usa `replaceState` en vez del router porque este módulo no lo
 *  importa (ver el letrero de abajo); no se pierde nada, porque la única cosa
 *  que lee este parámetro es esta misma función. */
export function apagarVistaReferidos() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('vista');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (e) { /* sin `history`: el borrado de abajo basta para esta carga */ }
  borrarVistaGuardada();
  oyentesDeVista.forEach((repintar) => { try { repintar(); } catch (e) { /* nada */ } });
}

function parametroDeVista() {
  try {
    return new URLSearchParams(window.location.search).get('vista') || '';
  } catch (e) { return ''; }
}

/** La vista activa de esta pantalla, o `null`. Las superficies que la leen
 *  (`/upgrade`, Ajustes, el Panel, el menú, el nav y el letrero) hacen
 *  exactamente esto, así que vive aquí y no copiado seis veces.
 *
 *  LA PUERTA SE EVALÚA EN CADA RENDER, y tiene que ser así: la sesión resuelve
 *  de forma asíncrona, y congelar la respuesta de `esVistaAdmin` en el primer
 *  render la dejaría fijada en `false` para siempre —cuando el `user` todavía es
 *  `null`— y la vista no se encendería nunca.
 *
 *  NADA SE GUARDA SIN LA PUERTA ABIERTA: el `sessionStorage` de quien no es
 *  administrador se queda vacío, pase lo que pase en la URL. */
export function useVistaReferidos(user) {
  const [, repintar] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    oyentesDeVista.add(repintar);
    return () => { oyentesDeVista.delete(repintar); };
  }, [repintar]);

  if (!esVistaAdmin(user)) return null;

  const cruda = parametroDeVista();
  if (cruda === 'off') { borrarVistaGuardada(); return null; }
  // Una vista firmada en la URL manda y se queda guardada. Un valor que no está
  // en la tabla se trata como si no viniera: no enciende nada y tampoco borra lo
  // que ya había, igual que antes un `?vista=loquesea` se comportaba como nada.
  if (datosDeVista(cruda)) { guardarVista(cruda); return cruda; }
  const guardada = leerVistaGuardada();
  return datosDeVista(guardada) ? guardada : null;
}
